import { useNavigate } from "react-router-dom";
import { Power, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTenant } from "@core/tenant";
import { useAuth, isProfileIncomplete } from "@core/auth";
import { redirectToAuth } from "@core/auth/authRedirect";
import { useBooking } from "@features/booking/state";
import { LanguageSelector } from "@shared/components/common";

interface BookingHeaderProps {
  showLanguageSelector?: boolean;
  /** Neither the logo nor an account button leaves the page: for a page whose URL is a credential. */
  withoutExits?: boolean;
}

const BookingHeader = ({ showLanguageSelector = true, withoutExits = false }: BookingHeaderProps) => {
  const { t } = useTranslation('profile');
  const { restaurant } = useTenant();
  const { resetBooking } = useBooking();
  const { authState, logout } = useAuth();
  const navigate = useNavigate();

  // When the profile completion gate is active the only useful action behind
  // the user icon is to log out — navigating to /profile would just bounce
  // back to /complete-profile via ProfileCompletionEnforcer.
  const showLogoutInsteadOfProfile =
    authState.isAuthenticated && isProfileIncomplete(authState);

  const handleLogoClick = () => {
    resetBooking();
    navigate('/');
  };

  const handleUserButtonClick = () => {
    if (showLogoutInsteadOfProfile) {
      logout();
      navigate('/', { replace: true });
      return;
    }
    if (authState.isAuthenticated) {
      navigate('/profile');
      return;
    }
    redirectToAuth('login');
  };

  const brand = restaurant?.customStyle?.headerImage ? (
    <img
      src={restaurant.customStyle.headerImage}
      alt={restaurant.name ?? 'Restaurant'}
      className="h-10 max-w-[180px] object-contain"
    />
  ) : (
    <p className="text-xl sm:text-2xl font-display tracking-wide text-center">
      {restaurant?.name ?? 'Restaurant'}
    </p>
  );

  return (
    <header className="booking-header px-6 sticky top-0 z-20 bg-card lg:translucent:bg-transparent max-lg:translucent:backdrop-blur-sm">
      <div className="w-10 flex justify-start">
        {showLanguageSelector ? (
          <LanguageSelector size="sm" />
        ) : (
          <div />
        )}
      </div>

      {withoutExits ? <div>{brand}</div> : (
        <button
          onClick={handleLogoClick}
          className="cursor-pointer"
          aria-label="Volver al inicio"
        >
          {brand}
        </button>
      )}

      <div className="w-10 flex justify-end">
        {!withoutExits && <button
          data-testid="header-account-button"
          className="w-10 h-10 -mr-2 rounded-full text-brand flex items-center justify-center hover:text-brand-dark hover:bg-brand/10 active:scale-[0.96] transition"
          onClick={handleUserButtonClick}
          aria-label={showLogoutInsteadOfProfile ? t('PROFILE_LOGOUT') : 'Mi perfil'}
        >
          {showLogoutInsteadOfProfile ? <Power size={18} /> : <UserRound size={18} />}
        </button>}
      </div>
    </header>
  );
};

export default BookingHeader;
